/**
 * Komponenta pro načítání textu na základě klíče z resource textů včetně formátování.
 * Komponenta využívá globální proměnnou messages, která musí být definovaná a musí již představovat aktuálně zvolený jazyk.
 * Pokud daný klíč v messages neexistuje, je vrácen řetězec '[klíč]'.
 * Pokud je vstupem klíč začínající znakem '^', je v případě neexistence klíče vráceno null.
 *
 * Příklad definice messages:
 *     var messages = {
 *         "a.b.c": "Osoba {0}"
 *     };
 *
 * Příklad formátu:
 *     i18n('a.b.c', person.displayName);
 **/
import {cs} from "./global-cs";
import {en} from "./global-en";

const messages = {
    cs,
    en
};

let customMessages = undefined;

String.format = function (format) {
    const args = Array.prototype.slice.call(arguments, 1);
    return format.replace(/{(\d+)}/g, function (match, number) {
        return typeof args[number] != 'undefined'
            ? args[number]
            : match
            ;
    });
};

function findByKey(lang, key) {
    if (customMessages && customMessages[lang] && customMessages[lang][key]) {
        return customMessages[lang][key];
    }  else if (messages[lang] && messages[lang][key]) {
        return messages[lang][key];
    } else {
        return null;
    }
}

function getForKey(lang, key, resolveNullAsText, ...params) {
    if (key[0] === '^') {
        key = key.substring(1, key.length);
        const text = findByKey(lang, key);
        return text != null ? String.format(text, ...params) : null;
    } else {
        let text = findByKey(lang, key);
        if (!text && key.indexOf("[") > 0) {    // pokus o odebrání všech polí, např. [3] a sestavení klíče bez nich (form.title.seznamKvalifikaci[0].umysl -> form.title.seznamKvalifikaci.umysl)
            while (true) {
                const i1 = key.indexOf("[");
                const i2 = key.indexOf("]");
                if (i1 >= 0 && i2 > 0 && i1 < i2) {
                    key = key.substring(0, i1) + key.substring(i2 + 1);
                } else {
                    break;
                }
            }
            if (key) {
                text = findByKey(lang, key);
            }
        }
        if (resolveNullAsText) {
            return text != null ? String.format(text, ...params) : "[" + key + "]";
        } else {
            return text != null ? String.format(text, ...params) : null;
        }
    }
}

function getMeta(text) {
    let list = text.match(/{(\d+)}/g);
    const all = {}
    list.forEach(i => {
        all[i.replaceAll(/{|}/g, '')] = true;
    });
    return {text, params: Object.keys(all).map(i => Number(i))};
}

function getForKeyMeta(lang, key, resolveNullAsText) {
    if (key[0] === '^') {
        key = key.substring(1, key.length);
        const text = findByKey(lang, key);
        return text != null ? getMeta(text) : null;
    } else {
        let text = findByKey(lang, key);
        if (!text && key.indexOf("[") > 0) {    // pokus o odebrání všech polí, např. [3] a sestavení klíče bez nich (form.title.seznamKvalifikaci[0].umysl -> form.title.seznamKvalifikaci.umysl)
            while (true) {
                const i1 = key.indexOf("[");
                const i2 = key.indexOf("]");
                if (i1 >= 0 && i2 > 0 && i1 < i2) {
                    key = key.substring(0, i1) + key.substring(i2 + 1);
                } else {
                    break;
                }
            }
            if (key) {
                text = findByKey(lang, key);
            }
        }
        if (resolveNullAsText) {
            return text != null ? getMeta(text) : null;
        } else {
            return text != null ? getMeta(text) : null;
        }
    }
}

export function i18nGetMeta(lang, key) {
    if (key) {
        // Key může být buď string nebo pole, pokud je pole, testují se postupně všechny klíče až se na nějaký narazí
        if (Object.prototype.toString.call(key) === "[object Array]") { // pole
            if (key.length === 0) { // nejsou definovány žádné klíče
                console.error("Resource text keys cannot be empty.");
                return null;
            }

            for (let a = 0; a < key.length; a++) {
                const oneKey = key[a];
                const meta = getForKeyMeta(lang, oneKey, false);
                if (meta !== null) {    // máme platný klíč
                    return meta;
                }
            }

            // Nenašli jsme platný klíč, vrátíme první jako nepřeložený text
            console.warn("Resource text for keys", key, "not found.");
            return getForKeyMeta(lang, key[0], true);
        } else {
            return getForKeyMeta(lang, key, true);
        }
    } else {
        return null;
    }
}

export function i18nCustom(lang, key, resolveNullAsText, ...params) {
    return getForKey(lang, key, resolveNullAsText, params);
}

export function i18n(lang, key, ...params) {
    if (key) {
        // Key může být buď string nebo pole, pokud je pole, testují se postupně všechny klíče až se na nějaký narazí
        if (Object.prototype.toString.call(key) === "[object Array]") { // pole
            if (key.length === 0) { // nejsou definovány žádné klíče
                console.error("Resource text keys cannot be empty.");
                return null;
            }

            for (let a = 0; a < key.length; a++) {
                const oneKey = key[a];
                const text = getForKey(lang, oneKey, false, params);
                if (text !== null) {    // máme platný klíč
                    return text;
                }
            }

            // Nenašli jsme platný klíč, vrátíme první jako nepřeložený text
            console.warn("Resource text for keys", key, "not found.");
            return getForKey(lang, key[0], true, params);
        } else {
            return getForKey(lang, key, true, params);
        }
    } else {
        return "<null>";
    }
}

export function i18nSetCustomMessages(data) {
    customMessages = data;
}

export function i18nGetCurrentLang() {
    return window.currentLanguage;
}

export function i18nSetCurrentLang(lang) {
    return window.lang = lang;
}
